import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import CloseIcon from "@mui/icons-material/Close";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { useShallow } from "zustand/react/shallow";
import { Button } from "components/common/Button";
import { LoadingState } from "components/common/LoadingState";
import { TooltipButton } from "components/common/TooltipButton";
import { Tooltip } from "components/common/Tooltip";
import { navigatorButtonClasses } from "components/common/navigatorStyles";
import {
  fetchOrgMembers,
  fetchOrgSpaces,
  isAbandoned,
  type Org,
  type OrgMember,
} from "@/lib/api/organizations";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { GeneralPanel } from "./GeneralPanel";
import { GovernancePanel } from "./GovernancePanel";
import { MembersPanel } from "./MembersPanel";
import { PlanUsagePanel } from "./PlanUsagePanel";
import { SpacesPanel } from "./SpacesPanel";

type Section = "general" | "members" | "spaces" | "governance" | "plan";

/// Fixed list per org type and role (DESIGN.md: "doesn't apply to this kind
/// of item at all" ⇒ leave out) — a personal org has no roster, and a plain
/// member governs nothing, so they get General alone (identity + Leave).
const sectionsFor = (org: Org): { id: Section; label: string }[] => [
  { id: "general", label: "General" },
  // A personal org has no roster and no admin distinct from its owner, so
  // neither the Members list nor the governance log would say anything its
  // owner does not already know.
  ...(org.is_personal || !isGovernor(org)
    ? []
    : [{ id: "members" as Section, label: "Members" }]),
  // Spaces stays for a personal org: its owner is its only governor, so this
  // is the one surface that can reappoint a manager on a space stranded there
  // (D49) — the rail cannot show a space nobody holds a grant on.
  ...(isGovernor(org) ? [{ id: "spaces" as Section, label: "Spaces" }] : []),
  ...(org.is_personal || !isGovernor(org)
    ? []
    : [{ id: "governance" as Section, label: "Governance" }]),
  ...(isGovernor(org)
    ? [{ id: "plan" as Section, label: "Plan & usage" }]
    : []),
];

const isGovernor = (org: Org) =>
  org.is_personal || org.role === "owner" || org.role === "org_admin";

/// Org settings — ONE component for both org types; `is_personal` decides
/// which menu items exist, never a second page. Reached from the switcher's
/// gear. Plain members see General only — identity, and the door out (D43).
export const OrgSettingsPage: React.FC = () => {
  const { org_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgs } = useOrgContextStore(useShallow((s) => ({ orgs: s.orgs })));

  // Nobody goes looking for an orphan, so the menu carries the count (D49).
  // Read for THIS org: the store holds the ACTIVE org's spaces, which is a
  // different org whenever these settings are opened from the org list.
  const [orphanCount, setOrphanCount] = useState(0);
  const reloadOrphanCount = useCallback(async () => {
    if (!org_id) return;
    try {
      const rows = await fetchOrgSpaces(org_id);
      setOrphanCount(rows.filter(isAbandoned).length);
    } catch {
      setOrphanCount(0);
    }
  }, [org_id]);
  useEffect(() => {
    void reloadOrphanCount();
  }, [reloadOrphanCount]);
  const org = orgs.find((o) => o.org_id === org_id);

  const sections = org ? sectionsFor(org) : [];
  // The section lives in the hash so a reload lands where you were.
  const fromHash = location.hash.replace("#", "") as Section;
  const section = sections.some((s) => s.id === fromHash)
    ? fromHash
    : "general";
  // Switching org carries the hash over, and the menu differs per org and
  // role — a section this org doesn't have must not leave the URL naming one
  // thing while the page shows another.
  useEffect(() => {
    if (org && location.hash && location.hash !== `#${section}`)
      navigate(`#${section}`, { replace: true });
  }, [org, location.hash, section, navigate]);

  const [members, setMembers] = useState<OrgMember[]>([]);
  // The roster AND the org row: `org.role` is what the Danger buttons and the
  // sole-owner notice read, and a role change here can be the CALLER's own —
  // promoting a second owner has to stop the page still calling you the last.
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);
  const reloadMembers = useCallback(async () => {
    if (!org_id) return;
    const [roster] = await Promise.all([
      fetchOrgMembers(org_id),
      refreshOrgs(),
    ]);
    setMembers(roster);
  }, [org_id, refreshOrgs]);
  useEffect(() => {
    void reloadMembers();
  }, [reloadMembers]);

  if (!org) {
    return orgs.length === 0 ? (
      <LoadingState label="Loading organization…" />
    ) : (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <span className="text-fg-regular">
          This organization does not exist here.
        </span>
        <Button variant="default" onClick={() => navigate("/overview")}>
          Back to overview
        </Button>
      </div>
    );
  }

  const setSection = (next: Section) => navigate(`#${next}`, { replace: true });

  return (
    <div className="flex h-full flex-col overflow-y-auto px-8">
      {/* Fixed top offset instead of the weighted centering spacers: the
          panels differ in height, so centering would bounce the whole column
          on every menu switch. Anchored in the top quarter, growth is
          downward only. A spacer rather than the column's padding, so the
          header below it can stick to the top of the scroll container. */}
      <div className="shrink-0" style={{ height: "12vh" }} />
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 shrink-0 bg-bg">
        <span className="mx-auto flex w-full max-w-3xl items-center gap-2 pb-2 item-height">
          <button
            type="button"
            onClick={() => navigate("/overview")}
            className={`${navigatorButtonClasses} cursor-pointer text-fg-muted-semibold`}
          >
            <span className="truncate">{org.name}</span>
          </button>
          <span className="flex items-center text-fg-muted-regular">/</span>
          <span className={`${navigatorButtonClasses} text-fg-semibold`}>
            Settings
          </span>
          <span className="ml-auto flex items-center">
            <TooltipButton
              title="Close settings"
              aria-label="Close organization settings"
              variant="default"
              size="xs"
              onClick={() => navigate("/overview")}
            >
              <CloseIcon fontSize="inherit" />
            </TooltipButton>
          </span>
        </span>
      </div>

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 pb-6">
        <div className="flex gap-6">
          {/* ── Menu ───────────────────────────────────────────── */}
          <nav
            aria-label="Organization settings sections"
            className="flex w-44 shrink-0 flex-col gap-px self-start"
          >
            {sections.map((s) => (
              <button
                key={s.id}
                aria-current={s.id === section}
                onClick={() => setSection(s.id)}
                className={`flex cursor-pointer items-center gap-2 rounded-app px-2 text-left item-height ${
                  s.id === section
                    ? "bg-selected-light text-fg-medium"
                    : "text-fg-muted-medium hover:bg-list-hover hover:text-fg"
                }`}
                style={
                  s.id === section
                    ? { boxShadow: "inset 2px 0 0 var(--color-accent)" }
                    : undefined
                }
              >
                <span className="flex-1">{s.label}</span>
                {s.id === "spaces" && orphanCount > 0 && (
                  <Tooltip
                    title={`${orphanCount} space(s) here have no manager. Nobody owns the models inside until someone is appointed.`}
                  >
                    <span className="flex items-center gap-1 text-warning">
                      <PersonOffOutlinedIcon sx={{ fontSize: 12 }} />
                      <span
                        className="font-mono"
                        style={{
                          fontSize: "var(--font-size-small)",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {orphanCount}
                      </span>
                    </span>
                  </Tooltip>
                )}
                {s.id === "members" && members.length > 0 && (
                  <span
                    className="font-mono text-fg-muted"
                    style={{
                      fontSize: "var(--font-size-small)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {members.length}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* ── Panel ──────────────────────────────────────────── */}
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            {section === "general" && (
              <GeneralPanel key={org.org_id} org={org} members={members} />
            )}
            {section === "members" && !org.is_personal && (
              <MembersPanel
                org={org}
                members={members}
                onChanged={reloadMembers}
              />
            )}
            {section === "spaces" && (
              <SpacesPanel org={org} members={members} />
            )}
            {section === "governance" && !org.is_personal && (
              <GovernancePanel org={org} />
            )}
            {section === "plan" && (
              <PlanUsagePanel org={org} memberCount={members.length} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
